<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useVModel } from '@vueuse/core'
import { IconObjectTag } from '@cypress-design/vue-icon'
import Checkbox from '@cypress-design/vue-checkbox'
import InputIcon, { type IconKey } from './InputIcon.vue'
import SelectInput from './SelectInput.vue'
import TextAreaInput from './TextAreaInput.vue'
import TextInput from './TextInput.vue'
import FormErrorAlert from './FormErrorAlert.vue'
import { validateEmail, validatePhoneNumber } from '@/utilities/validateInputs'

export interface FieldProps {
  name: string
  objectTypeId?: string
  label: string
  required: boolean
  fieldType: string
  hidden?: boolean
  defaultValue?: string
  defaultValues?: string[]
  options?: {
    label: string
    value: string
  }[]
  dependentFields?: {
    dependentCondition: {
      operator: string
      values: string[]
    }
    dependentField: FieldProps
  }[]
}

export interface Props extends FieldProps {
  name: string
  objectTypeId?: string
  label: string
  required: boolean
  fieldType: string
  showOptionalLabel?: boolean
  options?: {
    label: string
    value: string
  }[]
  dependentFields?: {
    dependentCondition: {
      operator: string
      values: string[]
    }
    dependentField: FieldProps
  }[]
  formId: string
  modelValue?: string
  error?: boolean
}

const modelBool = computed({
  get() {
    return !!props.modelValue
  },
  set(val) {
    if (val) {
      emit('update:modelValue', 'true')
    } else {
      emit('update:modelValue', '')
    }
  },
})

interface HubSpotFieldDefinitionBase {
  name: string
  label: string
  required: boolean
  fieldType: string
}

interface HubSpotFieldDefinitionDropDown extends HubSpotFieldDefinitionBase {
  fieldType: 'dropdown'
  options: {
    label: string
    value: string
  }[]
}

interface HubSpotFieldDefinitionRadio extends HubSpotFieldDefinitionBase {
  fieldType: 'radio' | 'radio'
  options: {
    label: string
    value: string
  }[]
}

type HubSpotFieldDefinition = HubSpotFieldDefinitionBase | HubSpotFieldDefinitionDropDown

function isDropDown(field: HubSpotFieldDefinition): field is HubSpotFieldDefinitionDropDown {
  return ['dropdown', 'radio'].includes(field.fieldType)
}

const NAME_ICON_TABLE: Record<string, IconKey> = {
  email: 'email',
  first_name: 'user',
  firstname: 'user',
  last_name: 'user',
  lastname: 'user',
  name: 'user',
  phone: 'smartphone',
  support_message: 'chat',
  company: 'building',
  company_name: 'building',
}

function getIcon(field: HubSpotFieldDefinitionBase): IconKey {
  return NAME_ICON_TABLE[field.name] ?? 'default'
}

const props = defineProps<Props>()

const emit = defineEmits(['update:modelValue'])

const value = useVModel(props, 'modelValue')

const errorText = ref('')

const labelInternal = computed(() => {
  const label = props.label || props.name
  if (!props.required && props.showOptionalLabel) {
    return `${label} (optional)`
  }
  return label
})

function validate() {
  errorText.value = !props.required || !!value.value ? '' : `${props.label} is required`
  if (props.fieldType === 'email' && value.value) {
    if (!validateEmail(value.value)) {
      errorText.value = 'Please enter a valid email address'
    }
  } else if (props.fieldType === 'phone' && value.value) {
    if (!validatePhoneNumber(value.value)) {
      errorText.value = 'Please enter a valid phone number. Spaces, dots, parentheses, and dashes are allowed.'
    }
  } else if (props.fieldType === 'single_checkbox' && props.name.includes('certify') && !value.value?.length) {
    errorText.value = `You must check this to continue.`
  }
  return !errorText.value.length
}

const errorAndText = computed(() => {
  return !!errorText.value.length || props.error
})

watch(value, () => {
  if (errorAndText.value) {
    validate()
  }
})

defineExpose({
  validate,
})

const fieldId = computed(() => {
  return `${props.formId}_${props.name}`
})
</script>

<template>
  <div
    :class="{
      'flex-1': fieldType !== 'dropdown',
      'w-full grow': fieldType === 'dropdown',
    }"
  >
    <Checkbox v-if="fieldType === 'single_checkbox'" v-model="modelBool" :label="label" :name="props.name" />
    <div
      v-else
      class="relative flex rounded-[4px] border"
      :class="{
        'border-red-300 ring ring-red-100': errorAndText,
        'border-gray-100': !errorAndText,
      }"
    >
      <template v-if="props.required && !props.showOptionalLabel">
        <div class="absolute left-[1px] top-[1px] z-10 h-[20px] w-[20px] overflow-hidden rounded-[3px]">
          <div class="h-0 w-0 border-[10px] border-gray-50 border-b-transparent border-r-transparent"></div>
        </div>
        <span
          class="absolute left-[3px] top-[3px] z-20 rounded-full text-center text-[14px] leading-[14px] text-red-400"
        >
          *
        </span>
      </template>
      <label
        :for="fieldId"
        class="absolute z-30 my-[8px] h-[calc(100%-16px)] w-[40px] border-r border-r-gray-50 px-[12px] py-[4px]"
      >
        <span class="sr-only">{{ labelInternal }}</span>
        <InputIcon
          v-if="!isDropDown(props)"
          :type="getIcon(props)"
          size="16"
          stroke-color="indigo-500"
          fill-color="indigo-100"
        />
      </label>
      <TextInput
        v-if="['single_line_text', 'email', 'phone'].includes(props.fieldType)"
        :id="fieldId"
        v-model="value"
        class="rounded-[3px]"
        :autocomplete="props.fieldType === 'email' ? 'email' : props.fieldType === 'phone' ? 'tel' : 'off'"
        :type="props.fieldType === 'email' ? 'email' : props.fieldType === 'phone' ? 'tel' : 'text'"
        :name="props.name"
        :label="labelInternal"
      />
      <TextAreaInput
        v-else-if="fieldType === 'multi_line_text'"
        :id="fieldId"
        v-model="value"
        class="rounded-[3px]"
        :name="props.name"
        :label="labelInternal"
      />
      <SelectInput
        v-else-if="isDropDown(props)"
        :id="fieldId"
        v-model="value"
        :name="props.name"
        :label="labelInternal"
        :options="props.options"
      >
        <template #option="{ label: optionLabel, value: optionValue }">
          <IconObjectTag
            fill-color="indigo-200"
            stroke-color="indigo-500"
            size="16"
            class="ml-[-4px] mr-[8px] shrink-0"
          />
          <div class="absolute left-0 h-[24px] w-[40px] group-hover:border-transparent" />
          <span class="pl-[16px]" :class="optionValue === undefined ? 'text-gray-400' : 'text-gray-900'">
            {{ optionLabel }}
          </span>
        </template>
      </SelectInput>
      <input
        v-else
        :id="fieldId"
        v-model="value"
        :name="props.name"
        type="text"
        class="block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-[12px]"
      />
    </div>
    <FormErrorAlert v-if="errorText" class="w-full">
      {{ errorText }}
    </FormErrorAlert>
  </div>
</template>
